"use client";
import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useMediaDeviceSelect } from "@livekit/components-react";
import { Logger } from "@shared/utils/logger";

export interface UsePreJoinDevicesOptions {
  initialVideoDeviceId?: string;
  initialAudioDeviceId?: string;
}

export interface UsePreJoinDevicesReturn {
  // Selected devices
  selectedVideoDeviceId: string;
  selectedAudioDeviceId: string;
  setSelectedVideoDeviceId: (id: string) => void;
  setSelectedAudioDeviceId: (id: string) => void;
  // Device lists
  videoDevices: MediaDeviceInfo[];
  audioDevices: MediaDeviceInfo[];
  // Menu state
  isVideoMenuOpen: boolean;
  isAudioMenuOpen: boolean;
  toggleVideoMenu: () => void;
  toggleAudioMenu: () => void;
  closeMenus: () => void;
  // Helpers
  getVideoDeviceLabel: () => string;
  getAudioDeviceLabel: () => string;
}

/**
 * Hook for managing device selection in pre-join view.
 * Handles device enumeration, selection state, and dropdown menus.
 */
export const usePreJoinDevices = ({
  initialVideoDeviceId = "",
  initialAudioDeviceId = ""
}: UsePreJoinDevicesOptions = {}): UsePreJoinDevicesReturn => {
  const t = useTranslations();
  const audioDeviceSelect = useMediaDeviceSelect({ kind: "audioinput" });
  const videoDeviceSelect = useMediaDeviceSelect({ kind: "videoinput" });

  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState<string>(
    initialVideoDeviceId || videoDeviceSelect.activeDeviceId || ""
  );
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string>(
    initialAudioDeviceId || audioDeviceSelect.activeDeviceId || ""
  );

  const [isVideoMenuOpen, setIsVideoMenuOpen] = useState(false);
  const [isAudioMenuOpen, setIsAudioMenuOpen] = useState(false);

  // Sync with LiveKit active device
  useEffect(() => {
    if (!selectedVideoDeviceId && videoDeviceSelect.activeDeviceId) {
      setSelectedVideoDeviceId(videoDeviceSelect.activeDeviceId);
    }
  }, [videoDeviceSelect.activeDeviceId, selectedVideoDeviceId]);

  useEffect(() => {
    if (!selectedAudioDeviceId && audioDeviceSelect.activeDeviceId) {
      setSelectedAudioDeviceId(audioDeviceSelect.activeDeviceId);
    }
  }, [audioDeviceSelect.activeDeviceId, selectedAudioDeviceId]);

  // Device enumeration
  useEffect(() => {
    const getDevices = async () => {
      try {
        if (navigator.mediaDevices?.enumerateDevices) {
          await navigator.mediaDevices.enumerateDevices();
        }
      } catch (e) {
        Logger.error("usePreJoinDevices", "Error enumerating devices", e);
      }
    };
    getDevices();

    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener("devicechange", getDevices);
    }

    return () => {
      if (navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener("devicechange", getDevices);
      }
    };
  }, []);

  const toggleVideoMenu = useCallback(() => {
    setIsVideoMenuOpen((prev) => !prev);
    setIsAudioMenuOpen(false);
  }, []);

  const toggleAudioMenu = useCallback(() => {
    setIsAudioMenuOpen((prev) => !prev);
    setIsVideoMenuOpen(false);
  }, []);

  const closeMenus = useCallback(() => {
    setIsVideoMenuOpen(false);
    setIsAudioMenuOpen(false);
  }, []);

  const getVideoDeviceLabel = useCallback(() => {
    const label = videoDeviceSelect.devices.find((d) => d.deviceId === selectedVideoDeviceId)?.label;
    return label === "Default" || label === "default"
      ? t("preJoin.defaultCamera")
      : label || t("preJoin.defaultCamera");
  }, [videoDeviceSelect.devices, selectedVideoDeviceId, t]);

  const getAudioDeviceLabel = useCallback(() => {
    const label = audioDeviceSelect.devices.find((d) => d.deviceId === selectedAudioDeviceId)?.label;
    return label === "Default" || label === "default"
      ? t("preJoin.defaultMicrophone")
      : label || t("preJoin.defaultMicrophone");
  }, [audioDeviceSelect.devices, selectedAudioDeviceId, t]);

  return {
    selectedVideoDeviceId,
    selectedAudioDeviceId,
    setSelectedVideoDeviceId,
    setSelectedAudioDeviceId,
    videoDevices: videoDeviceSelect.devices,
    audioDevices: audioDeviceSelect.devices,
    isVideoMenuOpen,
    isAudioMenuOpen,
    toggleVideoMenu,
    toggleAudioMenu,
    closeMenus,
    getVideoDeviceLabel,
    getAudioDeviceLabel
  };
};
